import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {KeypadPluginEvents} from "@nolimitcity/slot-launcher/bin/plugins/KeypadPlugin";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/text/TextStyleOptions";
import {Helper} from "@nolimitcity/slot-game/bin/utils/Helper";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {gsap} from "gsap";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {KennethMustDie} from "../KennethMustDie";
import {ISymbolValues} from "../server/IKMDData";

interface PayTableData {
    dispObj: PIXI.DisplayObject;
    name: string;
    resizeLevel: number
}

interface PayTableScaling {
    portrait: PIXI.Point;
    landscape: PIXI.Point;
}

interface MiniPayTableConfig {
    style?: TextStyleOptions;
    orientation_scale?: PayTableScaling;
    showBetValue?: boolean;
    extraArea?: number;
    bgAlpha?: number;
}

export class KMDMiniPayTable extends PIXI.Container implements StageLayer {

    private _valueText: PIXI.Text;
    private _copiedTarget: PIXI.Sprite;
    private _target: PIXI.DisplayObject | undefined;
    private _bgGraphic: PIXI.Graphics;
    private _tl: gsap.core.Timeline;
    private readonly _symbolValues: ISymbolValues;
    private _currentBetLvl: number = 1;
    private DEFAULT_TEXT_STYLE: TextStyleOptions = {
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 10,
        dropShadowDistance: 2,
        fontFamily: "Open Sans",
        fontWeight: FontWeight.BOLD,
        fontSize: 28,
        align: "left",
        wordWrap: true,
        wordWrapWidth: 400,
        fill: [
            "white",
        ]
    };
    private _effectiveHeightOfSprite: number;
    private _offsetAmount: number;

    constructor(values: ISymbolValues, config?: MiniPayTableConfig) {
        super();
        this.name = "KMDMiniPayTable";

        this._symbolValues = values;
        this.addBaseEventListeners();
        this.init();
    }

    private _oriScale: PayTableScaling = {
        portrait: new PIXI.Point(1, 1),
        landscape: new PIXI.Point(1, 1)
    };

    private get oriScale(): PIXI.Point {
        return NolimitApplication.isLandscape ? this._oriScale.landscape : this._oriScale.portrait;
    };

    private get boxHeight(): number {
        return this._effectiveHeightOfSprite + 10;
    }

    private get boxWidth(): number {
        return KennethMustDie.SYMBOL_WIDTH * 2 + 10;
    }

    private get leftPos(): boolean {
        return this.boxWidth + Math.round(this._copiedTarget.position.x) > 720;
    }

    private get leftPosOffset(): number {
        return (this.leftPos ? 1 : -1) * (this.boxWidth / 2 - this._copiedTarget.width / 2 - 10);
    }

    public onNewBet(): void {
        this._target = undefined;
        this._tl = this.setTimeline(this._tl, gsap.timeline(), true);
    }

    public onClicked(data: PayTableData): void {
        if (data.name && this._symbolValues[data.name]) {
            if (this.defineObjectFromData(data)) {
                this.onResize();

                this.animateClick();
            } else {
                this._tl = this.setTimeline(this._tl, gsap.timeline(), true);
            }
        }
    }

    public onResize(): void {
        if (this._target !== undefined) {
            this._tl = this.setTimeline(this._tl, gsap.timeline(), true);

            this.defineCopiedSprite();

            this.defineText();

            this.defineGraphic();
        }
    }

    private addBaseEventListeners(): void {
        // For fetching the start value.
        KennethMustDie.keypadPlugin.events.on(KeypadPluginEvents.DISPLAY_BET_UPDATE, (value: string) => {
            this._currentBetLvl = parseFloat(value);
        });
    }

    private init(): void {
        this._bgGraphic = this.addChild(new PIXI.Graphics());

        this.createCopySprite();
        this.createText("", this.DEFAULT_TEXT_STYLE);
        NolimitApplication.getLayerByName(this.name)?.addChild(this);
        this.onResize();
    }

    private createCopySprite(): void {
        this._copiedTarget = this.addChild(new PIXI.Sprite());
        this._copiedTarget.anchor.set(0.5, 0.5);
    }

    private createText(text: string, style: TextStyleOptions): void {
        this._valueText = this.addChild(new PIXI.Text("x" + text, style));
        this._valueText.alpha = 0;
    }

    private defineObjectFromData(data: PayTableData): boolean {
        this._effectiveHeightOfSprite = 150;
        if (data.resizeLevel === 1) {
            this._effectiveHeightOfSprite = 100;
        } else if (data.resizeLevel === 2) {
            this._effectiveHeightOfSprite = 75;
        }

        // #477
        let blurHelper: string = "";
        if (KennethMustDie.useCensor && KennethMustDie.symbolsToBeCensored.includes(data.name)) {
            blurHelper = "_blur";
        }

        this._target = data.dispObj;

        // Copy the targetTexture for display.
        const texture: PIXI.Texture = KennethMustDie.assetLoader.getTextures(data.name + "/miniPaytable/mg_" + this._effectiveHeightOfSprite + blurHelper)[0];

        // Set texture to be displayed.
        this._copiedTarget.texture = texture;

        // Find the symbol type.
        const sName: string = data.name ? data.name : texture.textureCacheIds[0].split("/")[0];
        return this.setText(sName);
    }

    private setText(symValueTarget: string): boolean {
        this._copiedTarget.alpha = !!this._symbolValues[symValueTarget] ? 1 : 0;

        if (!this._symbolValues[symValueTarget]) {
            return !!this._symbolValues[symValueTarget];
        }
        // Getting Connections.
        const maxConn: string[] = Object.keys(this._symbolValues[symValueTarget]).reverse().map((symbolName: string) => { // reverse is due to #388
            return symbolName;
        });
        // Getting Connection values.
        const maxConnValues: number[] = ArrayHelper.initArrayWithValues((maxConn.length), (i: number) => {
            return this._symbolValues[symValueTarget][parseInt(maxConn[i])];
        });

        this._valueText.text = "";

        maxConn.forEach((element: string, index: number) => {
            const amount: number = (maxConnValues[index] * this._currentBetLvl),
                text: string = (amount >= 10 ? KennethMustDie.apiPlugin.currency.format(amount, {precision: 0}) : KennethMustDie.apiPlugin.currency.format(amount)); // #388
            this._valueText.text += element + "|" + text;

            if (index + 1 !== maxConn.length) {
                this._valueText.text += "\n";
            }

            // #388
            this._offsetAmount = (amount >= 10 ? 3 : 0);
        });

        return true;
    }

    private animateClick(): void {
        this.alpha = 1;
        this._tl.fromTo([this._bgGraphic, this._valueText], {alpha: 0}, {duration: 0.35, alpha: 1}, 0);
        this._tl.to(this, {duration: 0.5, alpha: 0}, 2.25);
        this._tl.add(() => {
            this.interactive = false;
        });
        this._tl.play();

        this.interactive = true;
    }

    private defineText(): void {
        this._valueText.style.fontSize = this.oriScale.x * (this._effectiveHeightOfSprite === 75 ? 22 : (<number>this.DEFAULT_TEXT_STYLE.fontSize));
        // #401 -> 1
        while (this._valueText.width > 120) {
            this._valueText.style.fontSize--;
        }
        this._valueText.updateTransform();

        this._valueText.position = this._copiedTarget.position;

        let targetTxtX2: number = this._copiedTarget.position.x + this._copiedTarget.width / 2;
        if (this.leftPos) {
            targetTxtX2 = this._copiedTarget.position.x - (this._valueText.width + this._copiedTarget.width / 2);
        }

        let offset: number = 5;
        if (this._effectiveHeightOfSprite === 75) {
            offset = 12;
        }
        offset += this._offsetAmount;

        targetTxtX2 += (this.leftPos ? -offset : offset) * this._valueText.style.fontSize / 10;

        const targetTxtY2: number = this._copiedTarget.position.y - this._valueText.height / 2;
        this._valueText.position.set(targetTxtX2, targetTxtY2);
    }

    private defineCopiedSprite(): void {
        this._copiedTarget.scale = this.oriScale;
        const copyTargetPos: PIXI.Point = Helper.copyLocalPosition(this._copiedTarget, this._target!);
        this._copiedTarget.position = new PIXI.Point(copyTargetPos.x, copyTargetPos.y);

        const pivotOffset: PIXI.Point = MathHelper.pointSub(new PIXI.Point(0.5, 0.5), (<PIXI.Sprite>this._target).anchor ? (<PIXI.Sprite>this._target).anchor : new PIXI.Point(0.5, 0.5)),
            pivotPosOffset: PIXI.Point = MathHelper.pointMul(pivotOffset, new PIXI.Point(this._copiedTarget.width, this._effectiveHeightOfSprite));

        this._copiedTarget.position = MathHelper.pointAdd(<PIXI.Point>this._copiedTarget.position, pivotPosOffset);
    }

    private defineGraphic(): void {
        this._bgGraphic.position = this._copiedTarget.position;

        this._bgGraphic.clear();
        this._bgGraphic.lineStyle(3, 0xfc00ff, 1);
        this._bgGraphic.beginFill(0x000000, 1);
        this._bgGraphic.drawRoundedRect(
            -this.boxWidth / 2,
            -this.boxHeight / 2,
            this.boxWidth,
            this.boxHeight, 20);
        this._bgGraphic.endFill();
        this._bgGraphic.position.x -= this.leftPosOffset;
    }

    private setTimeline(tl: gsap.core.Timeline | null, tlReplace: gsap.core.Timeline, playToEnd?: boolean): gsap.core.Timeline {
        if (!tl) {
            return tlReplace;
        }

        if (playToEnd) {
            tl.totalProgress(1);
        }
        tl.pause();
        tl.kill();
        tl.clear(true);
        tl = null;

        return tlReplace;
    }
}
